import { AceCard } from '../AceCard';
import { Tabs, Tab, Icon, TabId } from '@blueprintjs/core';
import styles from './ApplicantCardView.module.css';
import { ApplicantDetails } from './ApplicantDetails';
import {
  useApplicationStatusUtils,
  useLoanApplication,
} from '@axo/shared/data-access/hooks';
import { useApplicantsForm } from '../../forms/applicants/form';
import {
  getApplicantsFormStore,
  SelectedApplicantTab,
  useApplicantsFormStore,
} from '../../forms/applicants/store';
import { useCallback, useEffect, useState } from 'react';
import { useTypedFlags } from '@axo/shared/services/feature-flags';
import { ACEFlags } from '../../utils/ACEFlags';
import { MarketCountryCode } from '@axo/shared/data-access/types/ace_index';

const CREATE_CO_APPLICANT_TAB_ID = 'create-co-applicant';

interface ApplicantCardViewProps {
  applicationID: string;
}

export const ApplicantCardView: React.FC<ApplicantCardViewProps> = ({
  applicationID,
}) => {
  const { data: applicationData } = useLoanApplication(applicationID);
  const { isFinalApplicationStatus } = useApplicationStatusUtils();

  const TABS_PARENT_ID = `application-data-tabs-${applicationID}`;

  const {
    isEditing,
    selectedApplicant,
    actions: { setSelectedApplicant },
  } = useApplicantsFormStore(applicationID);

  const hasCoApplicant =
    applicationData?.CoApplicantPersonID ||
    applicationData?.CoApplicantCustomerID;

  const { watch } = useApplicantsForm(applicationID);

  const [
    mainApplicantFirstName,
    mainApplicantLastName,
    coApplicantFirstName,
    coApplicantLastName,
  ] = watch([
    'MainApplicant.Person.FirstName',
    'MainApplicant.Person.LastName',
    'CoApplicant.Person.FirstName',
    'CoApplicant.Person.LastName',
  ]);

  const mainApplicantName =
    `${mainApplicantFirstName ?? ''} ${mainApplicantLastName ?? ''}`.trim() ||
    undefined;
  const coApplicantName =
    `${coApplicantFirstName ?? ''} ${coApplicantLastName ?? ''}`.trim() ||
    undefined;

  const [selectedTab, setSelectedTab] = useState<
    SelectedApplicantTab | typeof CREATE_CO_APPLICANT_TAB_ID
  >(selectedApplicant);

  const { flag_allow_add_co_applicant } = useTypedFlags(ACEFlags);

  // Currently Zmarta Broker in Finland doesn't support co-applicant and Axo
  // Broker requires authentication before adding one.
  const applicationSupportsCoApplicant =
    applicationData &&
    applicationData.MarketCountry !== MarketCountryCode.Finland;

  const canAddCoApplicant =
    flag_allow_add_co_applicant &&
    !hasCoApplicant &&
    isEditing &&
    !isFinalApplicationStatus(applicationData?.Status ?? undefined) &&
    applicationSupportsCoApplicant;

  useEffect(() => {
    const store = getApplicantsFormStore(applicationID);

    store.subscribe((state, prevState) => {
      if (state.selectedApplicant !== prevState.selectedApplicant) {
        setSelectedTab(state.selectedApplicant);
      }
    });
  });

  const onTabChange = useCallback(
    (tabID: TabId) => {
      if (tabID === CREATE_CO_APPLICANT_TAB_ID) {
        setSelectedTab(CREATE_CO_APPLICANT_TAB_ID);
      } else if (tabID === SelectedApplicantTab.CO_APPLICANT) {
        setSelectedTab(tabID);
        setSelectedApplicant(tabID);
      } else {
        setSelectedTab(SelectedApplicantTab.MAIN_APPLICANT);
        setSelectedApplicant(SelectedApplicantTab.MAIN_APPLICANT);
      }
    },
    [setSelectedApplicant],
  );

  return (
    <AceCard id={`ApplicantCard.${applicationID}`} title="Applicant profile">
      <Tabs
        fill
        id={TABS_PARENT_ID}
        onChange={onTabChange}
        selectedTabId={selectedTab}
        className={styles.tabs}
      >
        <Tab
          key="main-applicant"
          id={SelectedApplicantTab.MAIN_APPLICANT}
          title={
            <span className={styles.personTab}>
              <Icon icon="person" />
              <span className={styles.personTabLabel}>
                {mainApplicantName ?? 'Main Applicant'}
              </span>
            </span>
          }
          panelClassName={styles.tabPanel}
          panel={
            <div className={styles.container}>
              <ApplicantDetails
                applicationID={applicationID}
                personID={applicationData?.PersonID ?? ''}
                customerID={applicationData?.CustomerID ?? ''}
              />
            </div>
          }
        />
        {hasCoApplicant ? (
          <Tab
            key="co-applicant"
            id={SelectedApplicantTab.CO_APPLICANT}
            title={
              <span className={styles.personTab}>
                <Icon icon="people" />
                <span className={styles.personTabLabel}>
                  {coApplicantName ?? 'Co-Applicant'}
                </span>
              </span>
            }
            panelClassName={styles.tabPanel}
            panel={
              <div className={styles.container}>
                <ApplicantDetails
                  applicationID={applicationID}
                  personID={applicationData.CoApplicantPersonID ?? ''}
                  customerID={applicationData.CoApplicantCustomerID ?? ''}
                />
              </div>
            }
          />
        ) : null}
        {canAddCoApplicant ? (
          <Tab
            key={CREATE_CO_APPLICANT_TAB_ID}
            id={CREATE_CO_APPLICANT_TAB_ID}
            title={
              <span className={styles.personTab}>
                <Icon icon="plus" />
                <span className={styles.personTabLabel}>Add Co-Applicant</span>
              </span>
            }
            panelClassName={styles.tabPanel}
            panel={
              <div className={styles.container}>
                <ApplicantDetails
                  applicationID={applicationID}
                  personID=""
                  customerID=""
                />
              </div>
            }
          />
        ) : null}
      </Tabs>
    </AceCard>
  );
};
